/**
 * Phase 6e2a — ResultsFilter port (base_classes/results_filter.js, read in
 * full; §49.5).
 *
 * Blank/None values auto-unset on set(); `toURL()` skips blanks.
 *
 * [NEG]#119 NOTE — the spec text claims the `&` precedence bug makes
 * redundancy_id strippable and style_id always exempt. Verified against the
 * source (results_filter.js:43): `key != "fan_type" && key != "style_id" &
 * key != "redundancy_id"` parses as
 * `(key!="fan_type") && ((key!="style_id") & (key!="redundancy_id"))` —
 * `!=` binds tighter than `&` — so ALL THREE keys are exempt from blank
 * stripping AND the allowed_fields restriction (the bitwise `&` of two
 * booleans behaves like `&&` here). Observable URL payloads are identical
 * either way for blanks (toURL drops them); the difference is that exempt
 * keys bypass allowed_fields. Ported per code truth.
 */

const EXEMPT_KEYS = new Set(['fan_type', 'style_id', 'redundancy_id']);

const isBlank = (v: unknown): boolean =>
  v === undefined || v === null || v === '' || v === 'None' || v === 'none';

export class ResultsFilter {
  attributes: Record<string, unknown> = {};

  private allowedFields: Set<string> | null;

  constructor(
    attributes?: Record<string, unknown>,
    options?: { allowed_fields?: string[] | null }
  ) {
    this.allowedFields = options?.allowed_fields
      ? new Set(options.allowed_fields)
      : null;
    if (attributes) this.set(attributes);
  }

  set(attributes: Record<string, unknown>): void {
    const attrs = { ...attributes };
    Object.keys(attrs).forEach((key) => {
      if (!EXEMPT_KEYS.has(key)) {
        if (this.allowedFields && !this.allowedFields.has(key)) {
          delete attrs[key];
          return;
        }
        if (isBlank(attrs[key])) {
          delete this.attributes[key];
          delete attrs[key];
        }
      }
    });
    Object.assign(this.attributes, attrs);
  }

  unset(key: string): void {
    delete this.attributes[key];
  }

  reset(newAttributes: Record<string, unknown>): void {
    this.attributes = {};
    this.set(newAttributes);
  }

  clear(): void {
    this.attributes = {};
  }

  toJSON(): Record<string, unknown> {
    return { ...this.attributes };
  }

  /**
   * An empty filter carrying this one's rules — the `allowed_fields`
   * restriction, and with it the blank-stripping `set` applies.
   *
   * `resetFilter` dry-runs its own `clear()` → pin → `set()` sequence on one of
   * these to tell "this is a change" from "these are keys `set` is about to
   * throw away": a caller passing `frame: undefined` or a null `filter_id` is
   * not asking for anything new, and comparing the raw argument counted those
   * keys and replayed a whole pass for a filter that never moved. Running the
   * real methods on a throwaway filter, rather than re-stating their rules
   * somewhere else, is what keeps the dry run and the pass from drifting.
   */
  emptyClone(): ResultsFilter {
    return new ResultsFilter(undefined, {
      allowed_fields: this.allowedFields ? [...this.allowedFields] : null,
    });
  }

  toURL(withQ = true): string {
    const query = Object.keys(this.attributes)
      .filter((key) => !isBlank(this.attributes[key]))
      .map(
        (key) => `${key}=${encodeURIComponent(String(this.attributes[key]))}`
      )
      .join('&');
    return query && withQ ? `?${query}` : query;
  }

  length(): number {
    return Object.keys(this.attributes).length;
  }
}
